/* ============================================
   CART DRAWER - Ajax Cart System
   ============================================ */

(function() {
  const FREE_SHIPPING_THRESHOLD = 9900; // $99 in cents
  const CURRENCY_FORMAT = '${{amount}}';

  const selectors = {
    drawer: '[data-cart-drawer]',
    close: '[data-cart-drawer-close]',
    toggle: '[data-action="toggle-cart"]',
    items: '[data-cart-items]',
    count: '[data-cart-drawer-count]',
    headerCount: '[data-cart-count]',
    subtotal: '[data-cart-subtotal]',
    checkoutTotal: '[data-cart-checkout-total]',
    footer: '[data-cart-footer]',
    empty: '[data-cart-empty]',
    upsell: '[data-cart-upsell]',
    shippingBar: '[data-shipping-bar]',
    shippingText: '[data-shipping-text]',
    shippingRemaining: '[data-shipping-remaining]',
    shippingFill: '[data-shipping-fill]',
    shippingQualified: '[data-shipping-qualified]',
    noteInput: '[data-cart-note]',
    decrease: '[data-action="decrease-cart"]',
    increase: '[data-action="increase-cart"]',
    remove: '[data-action="remove-cart"]',
  };

  function init() {
    bindDrawerToggle();
    bindCartActions();
    bindNoteUpdate();
    interceptAddToCartForms();
  }

  // --- Drawer open/close ---
  function bindDrawerToggle() {
    document.querySelectorAll(selectors.toggle).forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        openDrawer();
      });
    });

    document.querySelectorAll(selectors.close).forEach(btn => {
      btn.addEventListener('click', () => closeDrawer());
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeDrawer();
    });
  }

  function openDrawer() {
    const drawer = document.querySelector(selectors.drawer);
    if (!drawer) return;
    drawer.hidden = false;
    requestAnimationFrame(() => {
      drawer.classList.add('is-open');
      document.body.classList.add('cart-open');
    });
  }

  function closeDrawer() {
    const drawer = document.querySelector(selectors.drawer);
    if (!drawer) return;
    drawer.classList.remove('is-open');
    document.body.classList.remove('cart-open');
    setTimeout(() => { drawer.hidden = true; }, 350);
  }

  // --- Cart actions (qty change, remove) ---
  function bindCartActions() {
    document.addEventListener('click', (e) => {
      const decreaseBtn = e.target.closest(selectors.decrease);
      const increaseBtn = e.target.closest(selectors.increase);
      const removeBtn = e.target.closest(selectors.remove);

      if (decreaseBtn) {
        e.preventDefault();
        const line = parseInt(decreaseBtn.dataset.line);
        const qtyEl = decreaseBtn.closest('.cart-drawer__item').querySelector('[data-qty-value]');
        const currentQty = parseInt(qtyEl?.textContent || 1);
        updateCartLine(line, Math.max(0, currentQty - 1));
      }

      if (increaseBtn) {
        e.preventDefault();
        const line = parseInt(increaseBtn.dataset.line);
        const qtyEl = increaseBtn.closest('.cart-drawer__item').querySelector('[data-qty-value]');
        const currentQty = parseInt(qtyEl?.textContent || 1);
        updateCartLine(line, currentQty + 1);
      }

      if (removeBtn) {
        e.preventDefault();
        const line = parseInt(removeBtn.dataset.line);
        updateCartLine(line, 0);
      }
    });
  }

  // --- Intercept add to cart forms ---
  function interceptAddToCartForms() {
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('form[action="/cart/add"]');
      if (!form) return;
      e.preventDefault();

      const formData = new FormData(form);
      const data = {
        id: formData.get('id'),
        quantity: parseInt(formData.get('quantity')) || 1
      };

      addToCart(data);
    });
  }

  // --- API calls ---
  async function addToCart(data) {
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [data] })
      });
      if (!res.ok) throw new Error('Add to cart failed');
      await refreshCart();
      openDrawer();
    } catch (err) {
      console.error('Cart add error:', err);
    }
  }

  async function updateCartLine(line, quantity) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: line, quantity: quantity })
      });
      if (!res.ok) throw new Error('Cart update failed');
      await refreshCart();
    } catch (err) {
      console.error('Cart update error:', err);
    }
  }

  async function refreshCart() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      renderCart(cart);
    } catch (err) {
      console.error('Cart refresh error:', err);
    }
  }

  // --- Note update ---
  function bindNoteUpdate() {
    let noteTimeout;
    document.addEventListener('input', (e) => {
      if (!e.target.matches(selectors.noteInput)) return;
      clearTimeout(noteTimeout);
      noteTimeout = setTimeout(() => {
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: e.target.value })
        });
      }, 500);
    });
  }

  // --- Render cart ---
  function renderCart(cart) {
    updateCounts(cart);
    updateShippingBar(cart);
    updateItems(cart);
    updateFooter(cart);
  }

  function updateCounts(cart) {
    document.querySelectorAll(selectors.count).forEach(el => {
      el.textContent = cart.item_count;
    });
    document.querySelectorAll(selectors.headerCount).forEach(el => {
      el.textContent = cart.item_count;
    });
  }

  function updateShippingBar(cart) {
    const remaining = Math.max(0, FREE_SHIPPING_THRESHOLD - cart.total_price);
    const percentage = Math.min(100, (cart.total_price / FREE_SHIPPING_THRESHOLD) * 100);
    const qualified = remaining === 0;

    const fill = document.querySelector(selectors.shippingFill);
    const text = document.querySelector(selectors.shippingText);
    const remainingEl = document.querySelector(selectors.shippingRemaining);
    const qualifiedEl = document.querySelector(selectors.shippingQualified);

    if (fill) fill.style.width = percentage + '%';

    if (qualified) {
      if (text) text.hidden = true;
      if (qualifiedEl) qualifiedEl.hidden = false;
    } else {
      if (text) text.hidden = false;
      if (qualifiedEl) qualifiedEl.hidden = true;
      if (remainingEl) remainingEl.textContent = formatMoney(remaining);
    }
  }

  function updateItems(cart) {
    const container = document.querySelector(selectors.items);
    if (!container) return;

    if (cart.item_count === 0) {
      container.innerHTML = `
        <div class="cart-drawer__empty" data-cart-empty>
          <p>Your cart is currently empty.</p>
          <a href="/collections/all" class="btn btn--primary">Shop All</a>
        </div>
      `;
      const upsell = document.querySelector(selectors.upsell);
      if (upsell) upsell.hidden = false;
      return;
    }

    const upsell = document.querySelector(selectors.upsell);
    if (upsell) upsell.hidden = true;

    let html = '';
    cart.items.forEach((item, index) => {
      const line = index + 1;
      const hasCompare = item.original_line_price > item.final_line_price;
      const variantTitle = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';

      html += `
        <div class="cart-drawer__item" data-cart-item data-variant-id="${item.variant_id}" data-line-index="${line}">
          <a href="${item.url}" class="cart-drawer__item-image">
            ${item.image ? `<img src="${getSizedImageUrl(item.image, '160x')}" alt="${escapeHtml(item.title)}" width="80" height="80" loading="lazy">` : ''}
          </a>
          <div class="cart-drawer__item-info">
            <a href="${item.url}" class="cart-drawer__item-title">${escapeHtml(item.product_title)}</a>
            ${variantTitle ? `<p class="cart-drawer__item-variant">${escapeHtml(variantTitle)}</p>` : ''}
            <div class="cart-drawer__item-price">
              <span>${formatMoney(item.final_line_price)}</span>
              ${hasCompare ? `<span class="cart-drawer__item-price-compare">${formatMoney(item.original_line_price)}</span>` : ''}
            </div>
            <div class="cart-drawer__item-qty">
              <button class="cart-drawer__qty-btn" data-action="decrease-cart" data-line="${line}">−</button>
              <span class="cart-drawer__qty-value" data-qty-value>${item.quantity}</span>
              <button class="cart-drawer__qty-btn" data-action="increase-cart" data-line="${line}">+</button>
            </div>
          </div>
          <button class="cart-drawer__item-remove" data-action="remove-cart" data-line="${line}" aria-label="Remove">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
            </svg>
          </button>
        </div>
      `;
    });

    container.innerHTML = html;
  }

  function updateFooter(cart) {
    const footer = document.querySelector(selectors.footer);
    if (!footer) return;

    if (cart.item_count === 0) {
      footer.hidden = true;
      return;
    }

    footer.hidden = false;
    const subtotal = footer.querySelector(selectors.subtotal);
    const checkoutTotal = footer.querySelector(selectors.checkoutTotal);
    if (subtotal) subtotal.textContent = formatMoney(cart.total_price);
    if (checkoutTotal) checkoutTotal.textContent = formatMoney(cart.total_price);
  }

  // --- Helpers ---
  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  function getSizedImageUrl(url, size) {
    if (!url) return '';
    return url.replace(/(\.[^.]+)$/, `_${size}$1`);
  }

  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
